import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import { Typography } from '@rbx/ui';
import {
  labelTranslKeyByMemberType,
  urlHashByMemberTypeDetailSection,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import {
  getInheritIteratorKey,
  IteratorName,
  classMemberTypeToIteratorName,
  orderedMemberTypes,
  ClassMemberType,
} from '@modules/apiReference/page/utils/apiReferenceUtils';
import { ReferenceType } from '@modules/client';
import useCharacteristicsTableContainerStyles from './CharacteristicsTableContainer.styles';
import {
  ClassReferenceProperties,
  ClassReferenceEvents,
  ClassReferenceMethods,
  ParentClassReference,
  ClassReferenceCallbacks,
} from '../../types';
import CharacteristicsIterator from './CharacteristicsIterator';
import CollapsibleCharacteristicsIterator from './CollapsibleCharacteristicsIterator';

interface CharacteristicsIteratorContainerProps {
  parents: ParentClassReference[] | null;
  properties: ClassReferenceProperties[] | null;
  events: ClassReferenceEvents[] | null;
  methods: ClassReferenceMethods[] | null;
  callbacks: ClassReferenceCallbacks[] | null;
}

type ClassMembers =
  | ClassReferenceProperties[]
  | ClassReferenceEvents[]
  | ClassReferenceMethods[]
  | ClassReferenceCallbacks[]
  | null;

const MemberTypeSectionHeader = ({
  id,
  headerTranslKey,
}: {
  id: string;
  headerTranslKey: string;
}) => {
  const { translate } = useTranslation();
  const {
    classes: { sectionHeader },
  } = useCharacteristicsTableContainerStyles();
  return (
    <Typography id={id} variant='h2' component='h2' className={sectionHeader}>
      {translate(headerTranslKey) || headerTranslKey}
    </Typography>
  );
};

const Members = ({ members }: { members: NonNullable<ClassMembers> }) => {
  const {
    classes: { members: memberClass },
  } = useCharacteristicsTableContainerStyles();
  return (
    <section className={memberClass}>
      <CharacteristicsIterator rows={members} />
    </section>
  );
};

const InheritedMembers = ({
  memberType,
  parents,
}: {
  memberType: ClassMemberType;
  parents: ParentClassReference[];
}) => {
  const { translate } = useTranslation();
  const {
    classes: { accordionGrid, inheritedMembers },
  } = useCharacteristicsTableContainerStyles();
  const memberFieldName = classMemberTypeToIteratorName[memberType];
  return !parents.length ? null : (
    <section className={inheritedMembers}>
      {parents.map((ref) => {
        const rows = ref[memberFieldName];
        return !rows?.length ? null : (
          <div key={ref.name} className={accordionGrid}>
            <CollapsibleCharacteristicsIterator
              name={ref.name}
              // type casting to non-nullabel type
              // bc we are assuming we filtered out nulls ahead of time
              rows={rows}
              memberType={memberType}
              iteratorKey={getInheritIteratorKey(memberFieldName, ref.name)}
              attribute={translate('Label.Properties') || 'Properties'}
            />
          </div>
        );
      })}
    </section>
  );
};

const CharacteristicsIteratorContainer: React.FC<
  React.PropsWithChildren<CharacteristicsIteratorContainerProps>
> = ({ parents, ...referenceMembersByIteratorName }) => {
  const hasMembers = useCallback(
    (iteratorName: IteratorName) =>
      Array.isArray(referenceMembersByIteratorName[iteratorName]) &&
      (referenceMembersByIteratorName[iteratorName] ?? []).length > 0,
    [referenceMembersByIteratorName],
  );
  const hasInheritedMembers = useCallback(
    (iteratorName: IteratorName) =>
      ((parents ?? []) as ParentClassReference[]).some((parent) => parent[iteratorName]?.length),
    [parents],
  );
  const hasSection = useCallback(
    (iteratorName: IteratorName) => hasMembers(iteratorName) || hasInheritedMembers(iteratorName),
    [hasMembers, hasInheritedMembers],
  );
  const existingSectionsOrderedMemberTypes = useMemo(
    () =>
      (orderedMemberTypes(ReferenceType.CLASS) as ClassMemberType[]).filter((memberType) =>
        hasSection(classMemberTypeToIteratorName[memberType]),
      ),
    [hasSection],
  );

  return (
    <Fragment>
      {(existingSectionsOrderedMemberTypes ?? []).map((memberType) => {
        const headerTranslKey = labelTranslKeyByMemberType[memberType];
        const iteratorName = classMemberTypeToIteratorName[memberType];
        const sectionId = urlHashByMemberTypeDetailSection[memberType].slice(1); // remove the starting '#'
        return (
          <Fragment key={memberType}>
            <MemberTypeSectionHeader id={sectionId} headerTranslKey={headerTranslKey} />
            {hasMembers(iteratorName) && (
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              <Members members={referenceMembersByIteratorName[iteratorName]!} />
            )}
            {hasInheritedMembers(iteratorName) && parents?.length && (
              <InheritedMembers memberType={memberType} parents={parents} />
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
};

export default CharacteristicsIteratorContainer;
